import React, {useEffect, useState} from "react";
import type {TocItem} from "../../services/blogRender";

// Section is "current" once its heading has scrolled past this offset from the top
const READING_LINE_PX = 140;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const jumpTo = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
  const target = document.getElementById(id);
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start"});
  window.history.replaceState(null, "", `#${id}`);
};

// Sticky "On this page" rail with the current section highlighted (desktop)
export const PostToc = ({items}: { items: TocItem[] }) => {
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const headings = items
      .map(({id}) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (headings.length === 0) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      let current = "";
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= READING_LINE_PX) current = heading.id;
        else break;
      }
      setActiveId(current);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", schedule, {passive: true});
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items]);

  return <nav aria-label="On this page" className="sticky top-28 max-h-[calc(100vh-9rem)] overflow-y-auto">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">On this page</p>
    <ul className="space-y-0.5">
      {items.map(({id, text}) => (
        <li key={id}>
          <a
            href={`#${id}`}
            onClick={event => jumpTo(event, id)}
            aria-current={activeId === id ? "location" : undefined}
            className={[
              "block rounded-r-lg border-l-2 py-1.5 pl-3 pr-2 text-sm leading-snug transition",
              activeId === id
                ? "border-blue-500 bg-blue-50 font-semibold text-blue-700"
                : "border-gray-200 text-gray-600 hover:bg-blue-50 hover:text-gray-900",
            ].join(" ")}
          >
            {text}
          </a>
        </li>
      ))}
    </ul>
  </nav>
}

// Collapsible list for tablet and phone, where there is no room for the rail
export const PostTocBox = ({items}: { items: TocItem[] }) => (
  <details className="my-6 rounded-xl border border-gray-200 bg-gray-50 lg:hidden">
    <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-semibold">
      In this article
      <span className="font-normal text-gray-500">{items.length} sections</span>
    </summary>
    <ul className="space-y-0.5 px-3 pb-3">
      {items.map(({id, text}) => (
        <li key={id}>
          <a href={`#${id}`} className="block rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-blue-50 hover:text-gray-900">
            {text}
          </a>
        </li>
      ))}
    </ul>
  </details>
)
